<template>
  <div :class="$style.wrapper">
    <component
      :is="DynamicTable" 
      :title="'Reports'"
      :exportButton="true"
      :headers="headers"
      :rows="originalRows"
      :loading-data="loadingData"
      :enable-pagination="true"
      :items-per-page="12"
    />
  </div>
</template>

<script setup>
  import { ref, onMounted, computed, resolveDynamicComponent } from 'vue';
  import { useStore } from 'vuex';
  import BaseTable from '@components/common/BaseTable.vue';
  import MobileTable from '@components/common/MobileTable.vue';
  import { formatToUSD } from '@/helpers/currencyFormatter';
  import { formatDate } from '@/helpers/dateFormatter';
  import {
    CalendarDateRangeIcon,
    ChartBarSquareIcon,
    PresentationChartLineIcon,
    PresentationChartBarIcon,
    CurrencyDollarIcon
  } from '@heroicons/vue/24/outline';
  import reportsService from '@/services/reportsService';

  const store = useStore();
  const originalRows = ref([]);
  const loadingData = ref(true);
  
  const isMobile = computed(() => store.getters['device/isMobile']);
  
  const DynamicTable = computed(() =>
    resolveDynamicComponent(isMobile.value ? MobileTable : BaseTable)
  );

  const headers = [
    { title: 'Date', icon: CalendarDateRangeIcon, key: 'date' },
    { title: 'Investiment', icon: ChartBarSquareIcon, key: 'investiment' },
    { title: 'PL', icon: PresentationChartLineIcon, key: 'pl' },
    { title: 'Var day', icon: PresentationChartBarIcon, key: 'varDay' },
    { title: 'Accumulated Profit', icon: CurrencyDollarIcon, key: 'profitsValue' },
    { title: 'Accumulated Profit (%)', icon: CurrencyDollarIcon, key: 'profitsPercent' },
    { title: 'Deposit/Withdrawal', icon: CurrencyDollarIcon, key: 'deposit' }
  ];

  const formatData = async (data) => {
    const cleanedRows = data.filter(item => item.exchange !== 'n');

    const formattedData = cleanedRows.map(item => ({
      date: formatDate(item.date),
      investiment: formatToUSD(item.start_value),
      pl: formatToUSD(item.pl),
      varDay: `${Math.trunc(item.roe_day * 100) / 100}%`,
      profitsValue: formatToUSD(item.roe_month_value),
      profitsPercent: `${Math.trunc(item.roe_month * 100) / 100}%`,
      deposit: formatToUSD(item.aporte)
    }));

    return formattedData;
  };

  const fetchData = async () => {
    try {
      loadingData.value = true
      const tableData = await reportsService.getReportTableData();
      originalRows.value = await formatData(tableData);
    } catch (error) {
      originalRows.value = [];
    } finally {
      loadingData.value = false;
    }
  };

  onMounted(async () => {
    await fetchData();
  });
</script>

<style module lang="scss">
  .wrapper {
    gap: 25px;
    display: flex;
    flex-wrap: wrap;
    margin-top: 20px;
    flex-direction: column;
  }
</style>